import React, { Component } from 'react';
import styled from 'styled-components';
import { Tooltip, Label, Checkbox, Grid } from '@inplayer-org/inplayer-ui';
import { MdInfoOutline } from 'react-icons/md';

// utils
import includes from 'lodash/includes';
import transformDataForMultiselect from 'utils/transformDataForMultiselect';

// Types
import { Follower } from 'ducks/merchantDucks/agency/types';
import { PaymentMethod } from 'ducks/merchantDucks/paymentMethods/types';
import { OptionsType, OptionTypeBase } from 'react-select/src/types';
import { Scopes } from 'pages/AdminPages/AdminMerchants/PaymentManagement/components/PaymentSetup';

// styled components
import { ItalicDescription } from 'components/SharedStyledElements';

// Components
import Select from 'components/Multiselect';
import Loader from 'components/Loader';
import ErrorMessage from 'components/ErrorMessage';

const { Cell, Container } = Grid;

const ContainerInheritance = styled(Container)`
  margin: 0.5rem 0 0 0;
`;

export const StyledTooltip = styled(Tooltip)`
  display: flex;
  width: fit-content;
`;

export const StyledLabel = styled(Label)`
  margin-right: 0.3rem;
`;

interface OwnProps {
  follower: Partial<Follower>;
  editMode: boolean;
  selectedMethods: { [key: number]: boolean };
  paymentMethods: Array<PaymentMethod>;
  onChange: (scopes: Array<string>, methods?: any) => any;
  handleCheckboxCheck: (id: number, e: boolean) => () => any;
  scopeOptions: OptionsType<OptionTypeBase>;
  isFetching: boolean;
  selectedScopes: Array<string>;
  isMethodsFetching: boolean;
  noMethods: boolean;
}

interface State {
  checkboxes: { [id: string]: boolean };
  scopes: Array<string>;
}

type Props = OwnProps;

export default class PaymentInheritance extends Component<Props, State> {
  handleSelectChange = (scopes: OptionsType<OptionTypeBase>) => {
    const { onChange, selectedMethods } = this.props;

    const scopeValues = scopes.map((scope) => (typeof scope === 'object' ? scope.value : scope) || '');

    if (includes(scopeValues, Scopes.INHERITANCE)) {
      onChange(scopeValues, selectedMethods);
    } else {
      onChange(scopeValues);
    }
  };

  render() {
    const {
      scopeOptions,
      isFetching,
      selectedScopes,
      paymentMethods,
      isMethodsFetching,
      noMethods,
      selectedMethods,
      handleCheckboxCheck,
    } = this.props;

    const showPaymentMethods =
      selectedScopes && selectedScopes.length > 0 && includes(selectedScopes, Scopes.INHERITANCE);
    const showErrorMessage = showPaymentMethods && noMethods;

    if (isMethodsFetching) {
      return <Loader />;
    }

    const selected = transformDataForMultiselect(scopeOptions, selectedScopes);

    return (
      <>
        <Container alignContent="space-around" columns={1}>
          <ContainerInheritance columns={3}>
            <Cell width={1} middle>
              <StyledTooltip
                tag="tooltip_inheritance_scopes"
                placement="right"
                content={`Select which settings will the new related account \ninherit from the master account.`}
              >
                <StyledLabel>Inheritance scopes</StyledLabel>
                <MdInfoOutline />
              </StyledTooltip>
            </Cell>
            <Cell width={2}>
              <Select
                tag="dropdown_inheritance_scopes"
                options={scopeOptions}
                isLoading={isFetching}
                placeholder="Select Inheritance scopes"
                selectedData={selected}
                name="scopes"
                onChange={this.handleSelectChange}
              />
            </Cell>
          </ContainerInheritance>
          {showPaymentMethods && (
            <>
              <Container columns={1}>
                <Cell height={2} middle>
                  <StyledTooltip
                    placement="right"
                    content={`You can specify which of the active payment methods to be\nset for the related account. In addition, with the payment\nsetup scope, the revenue share setup is inherited as well.`}
                  >
                    <StyledLabel>Payment Methods</StyledLabel>
                    <MdInfoOutline />
                  </StyledTooltip>
                </Cell>
                {paymentMethods && paymentMethods.length > 0 ? (
                  <Container columns={3}>
                    {paymentMethods.map((method) => {
                      const {
                        name,
                        payment_method: { id },
                      } = method;
                      return (
                        <Cell key={id.toString()} height={2} middle>
                          <Checkbox
                            name="checkbox"
                            id={id.toString()}
                            label={name}
                            checked={selectedMethods[id]}
                            onChange={handleCheckboxCheck(id, !selectedMethods[id])}
                          />
                        </Cell>
                      );
                    })}
                  </Container>
                ) : (
                  <ItalicDescription variant="p">There are not any payment methods to be set.</ItalicDescription>
                )}
              </Container>
            </>
          )}
          {showErrorMessage && <ErrorMessage>Please choose at least one payment method.</ErrorMessage>}
        </Container>
      </>
    );
  }
}
